import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import api, { photoUrl } from "@/api";
import { sym } from "@/currency";
import { X, Search, ShoppingCart, Send, Loader2, PackageCheck, FileText, Minus, Plus, Download, Check, RotateCw } from "lucide-react";
import { printHtml } from "@/printer";
import { salesDocHtml } from "@/docPrint";
import { dlocFull } from "@/docLocale";
import NumInput from "@/components/NumInput";

const fmt = (n) => (+n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function Stepper({ value, onChange, step = 1, testid }) {
  const v = +value || 0;
  return (
    <div className="flex items-center gap-1.5">
      <button onClick={() => onChange(Math.max(0, Math.round((v - step) * 1000) / 1000))}
        className="w-8 h-8 rounded-full bg-muted hover:bg-muted/70 flex items-center justify-center shrink-0" data-testid={`${testid}-minus`}>
        <Minus className="w-4 h-4" />
      </button>
      <NumInput type="number" min="0" step="any" className="field no-spin w-14 py-1 text-center mono text-sm" value={value || ""}
        placeholder="0" onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-qty`} />
      <button onClick={() => onChange(Math.round((v + step) * 1000) / 1000)}
        className="w-8 h-8 rounded-full bg-primary text-primary-foreground hover:opacity-90 flex items-center justify-center shrink-0" data-testid={`${testid}-plus`}>
        <Plus className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function NetworkShopModal({ link, t, onClose, dept: myDept, prefill }) {
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [docs, setDocs] = useState([]);
  const [tab, setTab] = useState("catalog");
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("all");
  const [cart, setCart] = useState({});
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [imp, setImp] = useState(false);
  const [sel, setSel] = useState({});
  const [impLive, setImpLive] = useState(true);
  const [editing, setEditing] = useState(null);

  const loadShop = () => api.networkShop(link.id).then(setData).catch(() => toast.error("Errore"));
  const loadOrders = () => api.networkMyOrders(link.id).then((r) => setOrders(r.orders || [])).catch(() => {});
  const loadDocs = () => api.networkDocs(link.id).then((r) => setDocs(r.documents || [])).catch(() => {});
  const reloadAll = () => { setData(null); loadShop(); loadOrders(); loadDocs(); };
  useEffect(() => {
    loadShop();
    loadOrders();
    loadDocs();
  }, [link.id]); // eslint-disable-line

  useEffect(() => {
    if (!prefill || !data) return;
    const c = {};
    (data.items || []).forEach((i) => {
      const q = +prefill[(i.name || "").trim().toLowerCase()] || 0;
      if (q > 0) c[i.sale_unit === "tray" ? `${i.id}:whole` : i.id] = q;
    });
    if (Object.keys(c).length) {
      setCart(c);
      toast.success(t("dsPrefillOk").replace("{n}", Object.keys(c).length));
    } else {
      toast.info(t("dsPrefillNone"));
    }
  }, [data]); // eslint-disable-line

  const openDoc = async (d) => {
    // apre subito la finestra al click (altrimenti il browser blocca il popup dopo l'await)
    const w = d.kind === "past" ? window.open("", "_blank") : null;
    if (w) { try { w.document.write("<p style='font-family:sans-serif;padding:20px'>Caricamento documento…</p>"); } catch {} }
    try {
      const full = await api.networkDocFull(link.id, d.kind, d.id);
      if (d.kind === "past") {
        if (full.photo_path) {
          const url = photoUrl(full.photo_path);
          if (w && !w.closed) w.location.replace(url);
          else window.open(url, "_blank");
          return;
        }
        if (w) w.close();
        printHtml(salesDocHtml(full, full.doc_type === "ddt" ? "ddt" : "invoice", sym(), false));
        return;
      }
      printHtml(salesDocHtml(full, d.kind === "ddt" ? "ddt" : "invoice", sym(), false));
    } catch (e) { if (w) w.close(); toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const items = data?.items || [];
  const departments = data?.departments || [];
  const deptName = (id) => departments.find((d) => d.id === id)?.name || id;
  const shown = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return items
      .filter((i) => dept === "all" || i.department === dept)
      .filter((i) => !qq || `${i.name} ${i.category || ""} ${i.sku || ""}`.toLowerCase().includes(qq));
  }, [items, q, dept]);
  const byCat = useMemo(() => {
    const g = {};
    shown.forEach((i) => { (g[i.category || "—"] = g[i.category || "—"] || []).push(i); });
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [shown]);

  const setQty = (key, qty) => setCart((c) => ({ ...c, [key]: qty }));
  const variantsOf = (i) => i.sale_unit === "tray"
    ? [
        { k: `${i.id}:whole`, variant: null, lbl: t("trayWhole"), price: i.price },
        { k: `${i.id}:half`, variant: "half", lbl: t("trayHalf"), price: i.price / 2 },
        ...(i.tray_pieces > 0 ? [{ k: `${i.id}:slice`, variant: "slice", lbl: t("traySlice"), price: i.price / i.tray_pieces }] : []),
      ]
    : [{ k: i.id, variant: null, lbl: null, price: i.price }];

  const lines = [];
  items.forEach((i) => variantsOf(i).forEach((v) => {
    const qv = +cart[v.k] || 0;
    if (qv > 0) lines.push({ recipe_id: i.id, quantity: qv, variant: v.variant, price: v.price });
  }));
  const total = lines.reduce((s, l) => s + l.quantity * l.price, 0);
  const count = lines.reduce((s, l) => s + l.quantity, 0);

  const startEdit = (o) => {
    const c = {};
    (o.items || []).forEach((li) => {
      const prod = items.find((x) => x.id === li.recipe_id)
        || items.find((x) => (x.name || "").trim().toLowerCase() === (li.name || "").trim().toLowerCase());
      if (!prod) return;
      const key = prod.sale_unit === "tray" ? `${prod.id}:${li.variant || "whole"}` : prod.id;
      c[key] = (+c[key] || 0) + (+li.quantity || 0);
    });
    setCart(c);
    setDate(o.delivery_date || "");
    setNotes(o.notes || "");
    setEditing(o);
    setTab("catalog");
    if (Object.keys(c).length < (o.items || []).length) toast.info(t("netEditPartial"));
  };
  const cancelEdit = () => { setEditing(null); setCart({}); setDate(""); setNotes(""); };

  const send = async () => {
    if (lines.length === 0) return;
    setBusy(true);
    try {
      if (editing) {
        const r = await api.networkEditOrder(link.id, editing.id, {
          items: lines.map((l) => ({ recipe_id: l.recipe_id, quantity: l.quantity, variant: l.variant })),
          delivery_date: date || null,
          notes: notes.trim() || null,
        });
        toast.success(`${t("netEditOk")} — ${sym()} ${fmt(r.total)}`);
        cancelEdit();
        loadOrders();
        setTab("orders");
        setBusy(false);
        return;
      }
      const r = await api.networkPlaceOrder(link.id, {
        items: lines.map((l) => ({ recipe_id: l.recipe_id, quantity: l.quantity, variant: l.variant })),
        delivery_date: date || null,
        notes: notes.trim() || null,
        from_dept: myDept || null,
      });
      if (myDept) {
        try {
          const itemsPayload = lines.map((l) => {
            const i = items.find((x) => x.id === l.recipe_id) || {};
            const suffix = l.variant === "half" ? ` (${t("trayHalf")})` : l.variant === "slice" ? ` (${t("traySlice")})` : "";
            return { name: `${i.name || ""}${suffix}`, unit: l.variant ? "pcs" : (i.sale_unit || "pcs"), quantity: l.quantity, price: l.price };
          });
          await api.dsTrackOrder(myDept, { link_id: link.id, order_ids: (r.orders || []).map((o) => o.id), total, items: itemsPayload });
        } catch { /* tracking best-effort */ }
      }
      toast.success(t("netShopSentOk").replace("{name}", link.other_name));
      setCart({}); setNotes("");
      loadOrders();
      setTab("orders");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const STATUS = { pending: ["⏳", "bg-amber-100 text-amber-700"], confirmed: ["✅", "bg-emerald-100 text-emerald-700"],
    ready: ["📦", "bg-sky-100 text-sky-700"], delivered: ["🚚", "bg-muted text-muted-foreground"],
    rejected: ["❌", "bg-destructive/10 text-destructive"], completed: ["✔️", "bg-muted text-muted-foreground"] };

  const selIds = Object.keys(sel).filter((k) => sel[k]);
  const toggleSel = (i) => { if (!i.in_catalog) setSel((s) => ({ ...s, [i.id]: !s[i.id] })); };
  const selectMany = (prods, on) => setSel((s) => {
    const n = { ...s };
    prods.forEach((p) => { if (!p.in_catalog) n[p.id] = on; });
    return n;
  });
  const doImport = async () => {
    if (selIds.length === 0) return;
    setBusy(true);
    try {
      const r = await api.networkImportCatalog(link.id, { recipe_ids: selIds, live: impLive });
      toast.success(t("netImpDone").replace("{n}", r.created) + (r.skipped > 0 ? ` · ${t("netImpSkipped").replace("{n}", r.skipped)}` : ""));
      setSel({}); setImp(false);
      loadShop();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[80] bg-background flex flex-col" data-testid="netshop-modal">
      <div className="px-4 md:px-8 pt-4 pb-2 border-b border-border/60 bg-card">
        <div className="flex items-center gap-3">
          <ShoppingCart className="w-5 h-5 text-sky-600 shrink-0" />
          <h2 className="display text-lg md:text-xl font-bold flex-1 truncate">{t("netShopTitle").replace("{name}", link.other_name)}</h2>
          <button onClick={reloadAll} className="p-2 rounded-lg hover:bg-muted text-accent" title={t("portalReload")} data-testid="netshop-reload">
            <RotateCw className="w-5 h-5" />
          </button>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted" data-testid="netshop-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex gap-1.5 mt-2 flex-wrap">
          {[["catalog", `🛒 ${t("catalogLbl")}`], ["orders", `📋 ${t("netShopMyOrders")} (${orders.length})`], ["docs", `🧾 ${t("netShopDocs")} (${docs.length})`]].map(([k, lbl]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`pill text-xs font-semibold transition-colors ${tab === k ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/80"}`}
              data-testid={`netshop-tab-${k}`}>{lbl}</button>
          ))}
        </div>
      </div>

      {!data && <div className="py-16 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-muted-foreground" /></div>}

      {data && tab === "catalog" && (
        <>
          <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-8 py-3 pb-32">
            {editing && (
              <div className="flex items-center gap-2 flex-wrap mb-3 p-2.5 rounded-xl bg-amber-50 border border-amber-300" data-testid="netshop-edit-banner">
                <span className="text-xs font-bold text-amber-900 flex-1 min-w-[180px]">
                  ✏️ {t("netEditBanner").replace("{d}", editing.delivery_date || (editing.created_at || "").slice(0, 10))}
                </span>
                <button onClick={cancelEdit} className="pill text-[11px] bg-white border border-amber-300 font-semibold hover:bg-amber-100" data-testid="netshop-edit-cancel">{t("cancel")}</button>
              </div>
            )}
            <div className="flex gap-2 flex-wrap items-center mb-3">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <input className="field pl-9 w-full" placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="netshop-search" />
              </div>
              {[["all", t("invAllTab")], ...departments.map((d) => [d.id, d.name])].map(([k, lbl]) => (
                <button key={k} onClick={() => setDept(k)}
                  className={`pill text-[11px] font-semibold transition-colors ${dept === k ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/80"}`}
                  data-testid={`netshop-dept-${k}`}>{lbl}</button>
              ))}
              <button onClick={() => { setImp((v) => !v); setSel({}); }}
                className={`pill text-[11px] font-bold transition-colors inline-flex items-center gap-1 ${imp ? "bg-[#C5A880] text-[#0D1F3D]" : "bg-sky-600 text-white hover:bg-sky-700"}`}
                data-testid="netshop-import-toggle">
                <Download className="w-3.5 h-3.5" /> {imp ? t("cancel") : t("netImpBtn")}
              </button>
            </div>
            {imp && (
              <div className="flex items-center gap-2 flex-wrap mb-3 p-2.5 rounded-xl bg-sky-50 border border-sky-200" data-testid="netshop-import-bar">
                <span className="text-xs font-semibold text-sky-900 flex-1 min-w-[160px]">{t("netImpHint")}</span>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-sky-900 cursor-pointer select-none">
                  <input type="checkbox" checked={impLive} onChange={(e) => setImpLive(e.target.checked)} className="accent-sky-600" data-testid="netshop-import-live" />
                  🔄 {t("netImpLive")}
                </label>
                <button onClick={() => selectMany(shown, true)} className="pill text-[11px] bg-white border border-sky-300 font-semibold hover:bg-sky-100" data-testid="netshop-import-all">
                  {t("netImpAll")}
                </button>
                <button onClick={doImport} disabled={busy || selIds.length === 0}
                  className="btn-primary text-xs py-1.5 disabled:opacity-40" data-testid="netshop-import-confirm">
                  <Download className="w-3.5 h-3.5" /> {t("netImpCopy").replace("{n}", selIds.length)}
                </button>
              </div>
            )}
            {byCat.map(([cat, prods]) => (
              <div key={cat} className="mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <div className="text-[11px] uppercase tracking-widest font-bold text-[#C5873F]">{cat}</div>
                  {imp && prods.some((p) => !p.in_catalog) && (
                    <button onClick={() => selectMany(prods, !prods.filter((p) => !p.in_catalog).every((p) => sel[p.id]))}
                      className="pill text-[10px] bg-sky-100 text-sky-700 font-bold hover:bg-sky-200" data-testid={`netshop-import-cat-${cat}`}>
                      {prods.filter((p) => !p.in_catalog).every((p) => sel[p.id]) ? t("deselectAll") : t("netImpCat")}
                    </button>
                  )}
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {prods.map((i) => (
                    <div key={i.id} onClick={imp ? () => toggleSel(i) : undefined}
                      className={`card-earth p-3 ${i.available ? "" : "opacity-50"} ${imp ? (i.in_catalog ? "opacity-45" : "cursor-pointer") : ""} ${imp && sel[i.id] ? "ring-2 ring-sky-500" : ""}`}
                      data-testid={`netshop-row-${i.id}`}>
                      <div className="flex items-start gap-3">
                        {imp && (
                          <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 mt-0.5 ${sel[i.id] ? "bg-sky-600 border-sky-600 text-white" : "border-muted-foreground/40 bg-white"}`}
                            data-testid={`netshop-import-check-${i.id}`}>
                            {sel[i.id] && <Check className="w-3.5 h-3.5" />}
                          </span>
                        )}
                        {i.photo_path
                          ? <img src={photoUrl(i.photo_path)} alt={i.name} loading="lazy" decoding="async" className="w-14 h-14 rounded-xl object-cover shrink-0" />
                          : <div className="w-14 h-14 rounded-xl bg-muted shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold break-words leading-tight">{i.name}</div>
                          <div className="text-[10px] text-muted-foreground">{deptName(i.department)}{!i.available ? ` · ${t("outOfStock")}` : ""}</div>
                          <div className="mono text-xs font-bold mt-0.5 text-sky-700">
                            {i.price > 0 ? `${sym()} ${fmt(i.price)} / ${i.sale_unit === "tray" ? t("trayLbl") : i.sale_unit === "kg" ? "kg" : "pz"}` : t("netPriceTbd")}
                          </div>
                          {imp && i.in_catalog && <span className="pill text-[9px] bg-emerald-100 text-emerald-700 font-bold mt-1 inline-block">✓ {t("netImpOwned")}</span>}
                        </div>
                      </div>
                      {!imp && (
                        <div className="mt-2 space-y-1.5">
                          {variantsOf(i).map((v) => (
                            <div key={v.k} className="flex items-center justify-between gap-2">
                              {v.lbl && <span className="text-[11px] text-muted-foreground flex-1">{v.lbl} <span className="mono">({sym()} {fmt(v.price)})</span></span>}
                              <Stepper value={cart[v.k]} onChange={(n) => setQty(v.k, n)} step={i.sale_unit === "kg" ? 0.5 : 1} testid={`netshop-${v.k.replace(/:/g, "-")}`} />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {shown.length === 0 && <div className="py-10 text-sm text-muted-foreground text-center">{t("empty")}</div>}
          </div>
          <div className="fixed bottom-0 left-0 right-0 z-[85] bg-card border-t border-border/60 px-4 md:px-8 py-3">
            <div className="flex items-center gap-2 flex-wrap max-w-5xl mx-auto">
              <input type="date" className="field py-1.5 text-sm" value={date} onChange={(e) => setDate(e.target.value)} title={t("deliveryDate")} data-testid="netshop-date" />
              <input className="field flex-1 min-w-[120px] py-1.5 text-sm" placeholder={t("notes")} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="netshop-notes" />
              <span className="mono font-bold whitespace-nowrap" data-testid="netshop-total">{count > 0 ? `${count} · ` : ""}{sym()} {fmt(total)}</span>
              <button onClick={send} disabled={busy || lines.length === 0} className="btn-primary text-sm" data-testid="netshop-send">
                <Send className="w-4 h-4" /> {editing ? t("netEditSave") : t("netShopSend")}
              </button>
            </div>
          </div>
        </>
      )}

      {data && tab === "orders" && (
        <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-8 py-4" data-testid="netshop-orders">
          {orders.map((o) => {
            const [emoji, cls] = STATUS[o.status] || ["•", "bg-muted"];
            return (
              <div key={o.id} className="flex items-center gap-2 py-2 border-b border-border/40 text-sm flex-wrap" data-testid={`netshop-order-${o.id}`}>
                <span className={`pill text-[11px] font-bold ${cls}`}>{emoji} {t(o.status) || o.status}</span>
                <span className="text-muted-foreground text-xs">{(o.created_at || "").slice(0, 10)}</span>
                {o.delivery_date && <span className="pill text-[10px] bg-muted font-semibold">📅 {o.delivery_date}</span>}
                <span className="text-xs text-muted-foreground">{o.items_count} {t("items")}</span>
                <span className="flex-1" />
                {(o.status === "pending" || o.status === "confirmed") && !o.received && (
                  <button onClick={() => startEdit(o)} className="pill text-[11px] bg-sky-100 text-sky-700 font-bold hover:bg-sky-200" data-testid={`netshop-edit-${o.id}`}>
                    ✏️ {t("edit")}
                  </button>
                )}
                {o.received && <span className="pill text-[10px] bg-emerald-100 text-emerald-700 font-bold" title={o.doc_number || ""}><PackageCheck className="w-3 h-3 inline" /> {t("netShopReceived")}</span>}
                <span className="mono font-bold">{sym()} {fmt(o.total)}</span>
              </div>
            );
          })}
          {orders.length === 0 && <div className="py-10 text-sm text-muted-foreground text-center">{t("empty")}</div>}
        </div>
      )}

      {data && tab === "docs" && (
        <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-8 py-4" data-testid="netshop-docs">
          {docs.map((d) => {
            const isDdt = d.kind === "ddt" || (d.kind === "past" && d.doc_type === "ddt");
            return (
              <button key={`${d.kind}-${d.id}`} onClick={() => openDoc(d)}
                className="w-full flex items-center gap-2 py-2.5 border-b border-border/40 text-sm text-left hover:bg-muted/40 rounded-lg px-1"
                data-testid={`netshop-doc-${d.kind}-${d.id}`}>
                <FileText className={`w-4 h-4 shrink-0 ${d.kind === "past" ? "text-amber-600" : isDdt ? "text-sky-600" : "text-emerald-600"}`} />
                <span className={`pill text-[10px] font-bold shrink-0 ${d.kind === "past" ? "bg-amber-100 text-amber-700" : isDdt ? "bg-sky-100 text-sky-700" : "bg-emerald-100 text-emerald-700"}`}>
                  {d.kind === "past" ? "📜 " : ""}{isDdt ? "DDT" : t("invoice")}
                </span>
                <span className="mono font-bold">{d.number || "—"}</span>
                <span className="flex-1 text-muted-foreground truncate text-xs">{dlocFull(d.date)}</span>
                {d.total > 0 && <span className="mono text-xs font-semibold">{sym()} {fmt(d.total)}</span>}
                <span className="text-[10px] text-[#C5873F] font-bold">→</span>
              </button>
            );
          })}
          {docs.length === 0 && <div className="py-10 text-sm text-muted-foreground text-center">{t("empty")}</div>}
        </div>
      )}
    </div>,
    document.body
  );
}
